import apiClient from './api';

export type StaffDispatchStrategy = 'distance_first' | 'order_count_first' | 'balanced';

export interface StaffAutoDispatchConfig {
  id?: string;
  enabled: boolean;
  strategy: StaffDispatchStrategy;
  maxDispatchDistanceKm: number;
  responseTimeoutMinutes: number;
  maxRetryCount: number;
  dailyDispatchLimit: number;
  requireCertifiedStaff: boolean;
  onlyOnlineStaff: boolean;
  allowCrossRegion: boolean;
  autoAcceptAfterDispatch: boolean;
  respectServiceTimeWindow: boolean;
  fallbackToManual: boolean;
  dispatchRemark: string;
  createdBy?: string;
  updatedBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

type RawStaffAutoDispatchConfig = Omit<StaffAutoDispatchConfig, 'strategy'> & {
  strategy?: StaffDispatchStrategy | 'level_first';
};

export const createDefaultStaffAutoDispatchConfig = (): StaffAutoDispatchConfig => ({
  enabled: true,
  strategy: 'balanced',
  maxDispatchDistanceKm: 15,
  responseTimeoutMinutes: 5,
  maxRetryCount: 3,
  dailyDispatchLimit: 25,
  requireCertifiedStaff: true,
  onlyOnlineStaff: true,
  allowCrossRegion: false,
  autoAcceptAfterDispatch: false,
  respectServiceTimeWindow: true,
  fallbackToManual: true,
  dispatchRemark: '优先分配给距离近、接单量合适且当前可接单的服务人员。',
});

const normalizeStrategy = (
  strategy?: StaffDispatchStrategy | 'level_first',
): StaffDispatchStrategy => {
  if (strategy === 'level_first') {
    return 'order_count_first';
  }
  return strategy ?? 'balanced';
};

const normalizeConfig = (data?: Partial<RawStaffAutoDispatchConfig>): StaffAutoDispatchConfig => {
  const defaults = createDefaultStaffAutoDispatchConfig();
  return {
    ...defaults,
    ...data,
    strategy: normalizeStrategy(data?.strategy),
  };
};

export const getStaffAutoDispatchConfig = async (): Promise<StaffAutoDispatchConfig> => {
  const response = await apiClient.get<RawStaffAutoDispatchConfig>('/staff-auto-dispatch-config');
  return normalizeConfig(response.data);
};

export const saveStaffAutoDispatchConfig = async (
  config: StaffAutoDispatchConfig,
): Promise<StaffAutoDispatchConfig> => {
  const payload: RawStaffAutoDispatchConfig = {
    ...config,
    strategy: normalizeStrategy(config.strategy),
  };
  const response = await apiClient.put<RawStaffAutoDispatchConfig>('/staff-auto-dispatch-config', payload);
  return normalizeConfig(response.data);
};
